'use client'

import { faSnowflake } from '@fortawesome/pro-solid-svg-icons'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { Switch } from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import { FeatureFlags } from '@/helpers/featureFlags'

import { FiltersButtonIcon } from '../shared/listings-ui'
import { useFreezeSortToggle } from './useFreezeSortToggle'

const ToggleContainer = styled.label<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.07rem 1rem;
  border-radius: 0.25rem;
  border: 0.0625rem solid
    ${({ $active }) =>
      $active
        ? Colors.Semantic.BStock.default.lightBackground
        : Colors.Semantic.Neutral.medium_grey};
  background-color: ${({ $active }) =>
    $active ? Colors.Semantic.BStock.backgroundSelected : 'transparent'};
  line-height: 1rem;
  cursor: pointer;
`

const ToggleLabel = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.black};
  white-space: nowrap;
`

const ScaledSwitch = styled(Switch)`
  transform: scale(0.75);
  transform-origin: center;
`

/**
 * "Freeze Sort" toggle (GLOB-4832): pauses Pusher-driven re-ranking of List
 * View results so buyers can evaluate rows sequentially without the list
 * reshuffling under them. In-row data (bid, price, countdown) keeps updating
 * live regardless of freeze state. Never persisted — resets to off on
 * refresh/navigation since it's backed by ListingSearchState.isSortFrozen.
 */
export const FreezeSortToggle = ({
  pageType,
}: {
  /** ALP ("All Auctions") vs. a seller Storefront — passed by the caller
   * since both render this same shared component (see ListView.tsx). */
  pageType: 'alp' | 'storefront'
}) => {
  const enableFreezeSort = useFlag(FeatureFlags.ENABLE_FREEZE_SORT)
  const { formatMessage } = useIntl()
  const { isFrozen, setFrozen } = useFreezeSortToggle(pageType)

  const label = formatMessage({
    id: isFrozen
      ? 'Listings.freezeSort.toggleFrozen'
      : 'Listings.freezeSort.toggle',
  })

  if (!enableFreezeSort) {
    return null
  }

  return (
    <ToggleContainer $active={isFrozen} title={label}>
      <FiltersButtonIcon icon={faSnowflake} />
      <ToggleLabel>{label}</ToggleLabel>
      <ScaledSwitch a11yLabel={label} value={isFrozen} onToggle={setFrozen} />
    </ToggleContainer>
  )
}
